import { Link } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Bookmark,
  CalendarClock,
  CalendarDays,
  Calculator,
  FolderKanban,
  BarChart3,
  UserCog,
  Workflow,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Permission } from '@kreart/shared';
import { useAuth } from '@/features/auth/useAuth';
import { LIBRARY_FEATURE_ENABLED } from '@/features/library/featureFlags';
import { OPERATIONS_PLANNER_FEATURE_ENABLED } from '@/features/project-planning/featureFlags';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission?: Permission;
  exact?: boolean;
  disabled?: boolean;
  /** Extra path prefixes that should also mark this item as active. */
  matchPrefixes?: string[];
}

interface SidebarNavProps {
  pathname: string;
  /** Called after a nav link is activated, e.g. to close the mobile drawer. */
  onNavigate?: () => void;
  collapsed?: boolean;
}

export function SidebarNav({ pathname, onNavigate, collapsed = false }: SidebarNavProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const items: NavItem[] = [
    { to: '/', label: t('nav.dashboard'), icon: LayoutDashboard, exact: true },
    {
      to: '/clients',
      label: t('clients.navLink'),
      icon: Users,
      permission: Permission.CLIENTS_LIST,
    },
    {
      to: '/cabinets',
      label: t('nav.catalog', { defaultValue: 'Catalog' }),
      icon: BookOpen,
      permission: Permission.CATALOG_READ,
      matchPrefixes: [
        '/panels',
        '/countertops',
        '/drawers',
        '/elements',
        '/lighting',
        '/hardware',
        '/admin/catalog',
      ],
    },
    {
      to: '/library',
      label: t('nav.library', { defaultValue: 'Librărie' }),
      icon: Bookmark,
      disabled: !LIBRARY_FEATURE_ENABLED,
    },
    {
      to: '/projects',
      label: t('projects.navLink'),
      icon: FolderKanban,
      permission: Permission.PROJECTS_LIST,
    },
    {
      to: '/operations-planner',
      label: t('nav.operationsPlanner', { defaultValue: 'Operations Planner' }),
      icon: CalendarDays,
      permission: Permission.PROJECT_PLANNING_READ,
      disabled: !OPERATIONS_PLANNER_FEATURE_ENABLED,
    },
    {
      to: '/operations-scheduler',
      label: t('nav.operationsScheduler', { defaultValue: 'Operations Scheduler' }),
      icon: Workflow,
      permission: Permission.PROJECT_PLANNING_READ,
    },
    {
      to: '/timesheet',
      label: t('nav.timesheet', { defaultValue: 'Pontaj lunar' }),
      icon: CalendarClock,
      permission: Permission.PROJECT_PLANNING_READ,
    },
    {
      to: '/project-organization',
      label: t('nav.projectOrganization', { defaultValue: 'Organizare proiect' }),
      icon: Calculator,
      permission: Permission.PROJECTS_READ_FINANCIALS,
    },
    { to: '/analytics', label: t('nav.analytics'), icon: BarChart3 },
    {
      to: '/users',
      label: t('nav.accounts', { defaultValue: 'Conturi' }),
      icon: UserCog,
      permission: Permission.USERS_LIST,
    },
  ];

  const has = (p?: Permission): boolean => !p || (user?.permissions.includes(p) ?? false);
  const isActive = (item: NavItem): boolean => {
    if (item.disabled) return false;
    if (item.exact) return pathname === item.to;
    if (pathname === item.to || pathname.startsWith(item.to + '/')) return true;
    return item.matchPrefixes?.some((p) => pathname === p || pathname.startsWith(p + '/')) ?? false;
  };

  return (
    <nav className="flex-1 px-2 py-2" aria-label={t('nav.primary', { defaultValue: 'Main' })}>
      <ul className="space-y-1">
        {items
          .filter((i) => has(i.permission))
          .map((item) => {
            const active = isActive(item);
            const Icon = item.icon;
            if (item.disabled) {
              return (
                <li key={item.to}>
                  <span
                    aria-disabled="true"
                    title={collapsed ? item.label : undefined}
                    className={`flex cursor-not-allowed items-center rounded-md py-2 text-sm text-sidebar-foreground/40 ${
                      collapsed ? 'justify-center px-2' : 'gap-3 px-3'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" focusable="false" />
                    <span className={collapsed ? 'sr-only' : undefined}>{item.label}</span>
                  </span>
                </li>
              );
            }
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  search={{}}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  aria-label={collapsed ? item.label : undefined}
                  title={collapsed ? item.label : undefined}
                  className={
                    `flex items-center rounded-md py-2 text-sm transition-colors ${
                      collapsed ? 'justify-center px-2' : 'gap-3 px-3'
                    } ` +
                    (active
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                      : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground')
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" focusable="false" />
                  <span className={collapsed ? 'sr-only' : undefined}>{item.label}</span>
                </Link>
              </li>
            );
          })}
      </ul>
    </nav>
  );
}
